import { Link } from "react-router-dom";
import { ArrowRight, Gem, ScanSearch, MessageSquareHeart } from "lucide-react";
import { Reveal } from "@/components/Motion";
import usePageTitle from "@/lib/usePageTitle";

const VALUES = [
  {
    icon: Gem,
    title: "Quality first",
    line: "Meticulously crafted work, every time — we treat every creation like a masterpiece.",
  },
  {
    icon: ScanSearch,
    title: "Attention to detail",
    line: "The small stuff is the whole point. We check our work like it's going on our own shelf.",
  },
  {
    icon: MessageSquareHeart,
    title: "Your satisfaction",
    line: "\u201cYour satisfaction is our greatest reward\u201d — it's been our promise since day one.",
  },
];

const STEPS = [
  { n: "01", t: "Share your idea", d: "Fill out the quote form — a sketch, a file, or a sentence is plenty." },
  { n: "02", t: "Get a personal quote", d: "A real person reviews it and replies by your preferred contact method. No automatic prices." },
  { n: "03", t: "We craft it", d: "Made in our own workshop, with a check-in if anything needs your approval." },
  { n: "04", t: "It's yours", d: "Pickup or delivery — [confirm your pickup & shipping options]." },
];

export default function About() {
  usePageTitle("About Us — Family-Owned Custom Creations | Skies The Limit Creations LLC");

  return (
    <div className="grain">
      <header className="relative overflow-hidden pb-6 pt-[130px] lg:pt-[160px]">
        <div aria-hidden className="pointer-events-none absolute -top-32 right-[-10%] h-[420px] w-[420px] rounded-full bg-ember/15 blur-[130px]" />
        <div className="mx-auto max-w-7xl px-5 sm:px-8" data-testid="about-header">
          <Reveal>
            <p className="eyebrow mb-5">Our story</p>
            <h1 className="max-w-3xl font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
              It started with a needle and thread.
            </h1>
          </Reveal>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 pb-24 sm:px-8 lg:pb-32">
        <div className="grid gap-12 lg:grid-cols-[1.15fr_0.85fr] lg:gap-16">
          <Reveal>
            <div className="space-y-6 text-base leading-relaxed text-mist sm:text-lg" data-testid="about-story">
              <p>
                Skies The Limit Creations LLC is a family-owned custom creation shop. We got our start with a passion
                for embroidery — stitching names, numbers, and logos for teams, schools, and businesses — and word
                traveled.
              </p>
              <p>
                As our customers' ideas grew, so did our toolbox. We added DTF printing for vibrant full-color
                designs, then sublimation for edge-to-edge finishes. Now our workshop does 3D printing and laser
                engraving too — which means if you can dream it up, there's a good chance we can help you make it
                real.
              </p>
              <p>
                What hasn't changed is how we work. Every order is handled by people who genuinely care about the
                details, and we're not satisfied until you are. That's the whole business plan.
              </p>
            </div>

            <div className="mt-12 grid gap-5 sm:grid-cols-3" data-testid="about-values">
              {VALUES.map((v, i) => (
                <Reveal key={v.title} delay={i * 0.1}>
                  <div className="card-panel h-full p-6">
                    <v.icon className="h-6 w-6 text-ember" aria-hidden />
                    <h2 className="mt-4 font-display text-base font-bold text-white">{v.title}</h2>
                    <p className="mt-2 text-xs leading-relaxed text-mist">{v.line}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <aside className="space-y-6">
              <figure className="overflow-hidden rounded-2xl border border-white/10">
                <img
                  src="/images/brand-banner.jpg"
                  alt="Skies The Limit Creations LLC brand artwork over a mountain sky — custom embroidery and print"
                  className="aspect-[16/10] w-full object-cover"
                  loading="lazy"
                />
                <figcaption className="bg-panel px-4 py-3 text-xs text-mist">Our brand — where the skies really are the limit</figcaption>
              </figure>
              <div className="placeholder-frame min-h-[300px] p-10" data-testid="about-makers-placeholder">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">[ Add a family photo ]</p>
                <p className="font-display text-lg font-bold text-white">Meet the makers</p>
                <p className="max-w-[260px] text-xs leading-relaxed text-mist">
                  [Add your family or team names and a photo — this card introduces the people behind the shop.]
                </p>
              </div>
              <div className="card-panel p-6" data-testid="about-family-badge">
                <p className="eyebrow mb-3">Family-owned &amp; operated</p>
                <p className="text-sm leading-relaxed text-mist">
                  Every request is read, quoted, and made by our family — no call centers, no ticket queues.
                </p>
              </div>
            </aside>
          </Reveal>
        </div>

        {/* Process */}
        <section className="mt-24 lg:mt-32" data-testid="about-process">
          <Reveal>
            <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">From idea to finished piece</h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div className="card-panel h-full p-7">
                  <p className="font-mono text-sm text-gold">{s.n}</p>
                  <h3 className="mt-4 font-display text-lg font-bold text-white">{s.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-mist">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <Reveal>
          <div className="mt-20 flex flex-col items-center gap-4 rounded-3xl border border-white/10 bg-panel px-6 py-14 text-center">
            <h2 className="max-w-xl font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Let's make your next thing together.
            </h2>
            <Link to="/quote" data-testid="about-quote-cta-button" className="btn-primary mt-3">
              Request a Quote <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </div>
        </Reveal>
      </main>
    </div>
  );
}
